import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import axios from "axios";

const emptyBank = {
  bankName: "",
  accountName: "",
  accountNumber: "",
  IBAN: "",
  currency: "",
};

const SignUp = () => {
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    businessLogo: null,
    businessName: "",
    businessAddress: "",
    businessABN: "",
    banks: [{ ...emptyBank }],
  });

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    // withCredentials: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      businessLogo: e.target.files[0] || null,
    }));
  };

  const handleBankChange = (index, e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedBanks = [...prev.banks];
      updatedBanks[index] = {
        ...updatedBanks[index],
        [name]: value,
      };

      return {
        ...prev,
        banks: updatedBanks,
      };
    });
  };

  const addBank = () => {
    setFormData((prev) => ({
      ...prev,
      banks: [...prev.banks, { ...emptyBank }],
    }));
  };

  const removeBank = (index) => {
    setFormData((prev) => {
      const updatedBanks = prev.banks.filter((_, i) => i !== index);

      return {
        ...prev,
        banks: updatedBanks.length ? updatedBanks : [{ ...emptyBank }],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");

    if (formData.password !== formData.confirmPassword) {
      setErrMsg("Passwords do not match");
      return;
    }

    const cleanedBanks = formData.banks
      .map((bank) => ({
        bankName: bank.bankName.trim(),
        accountName: bank.accountName.trim(),
        accountNumber: bank.accountNumber.trim(),
        IBAN: bank.IBAN.trim(),
        currency: bank.currency.trim(),
      }))
      .filter(
        (bank) =>
          bank.bankName ||
          bank.accountName ||
          bank.accountNumber ||
          bank.IBAN ||
          bank.currency,
      );

    try {
      setLoading(true);

      const data = new FormData();
      data.append("email", formData.email);
      data.append("password", formData.password);
      data.append("businessName", formData.businessName);
      data.append("businessAddress", formData.businessAddress);
      data.append("businessABN", formData.businessABN);

      if (formData.businessLogo) {
        data.append("businessLogo", formData.businessLogo);
      }

      data.append("banks", JSON.stringify(cleanedBanks));

      const res = await api.post("/users/signup", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data?.token) {
        localStorage.setItem("token", res.data.token);
      }

      navigate("/login");
    } catch (error) {
      setErrMsg(error?.response?.data?.error || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container style={{ maxWidth: "900px" }} className="py-4">
      <h2 className="mb-4">Sign Up</h2>

      {errMsg && <Alert variant="danger">{errMsg}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Business Logo</Form.Label>
          <Form.Control
            type="file"
            name="businessLogo"
            accept="image/*"
            onChange={handleFileChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Business Name</Form.Label>
          <Form.Control
            type="text"
            name="businessName"
            value={formData.businessName}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Business Address</Form.Label>
          <Form.Control
            type="text"
            name="businessAddress"
            value={formData.businessAddress}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>Business Registration No.</Form.Label>
          <Form.Control
            type="text"
            name="businessABN"
            value={formData.businessABN}
            onChange={handleChange}
          />
        </Form.Group>

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Bank Accounts</h4>
            <Button type="button" variant="outline-primary" onClick={addBank}>
              Add Bank
            </Button>
          </div>

          {formData.banks.map((bank, index) => (
            <Card className="mb-3 shadow-sm" key={`signup-bank-${index}`}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Bank #{index + 1}</h5>
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeBank(index)}
                  >
                    Remove
                  </Button>
                </div>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bank Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="bankName"
                        value={bank.bankName}
                        onChange={(e) => handleBankChange(index, e)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="accountName"
                        value={bank.accountName}
                        onChange={(e) => handleBankChange(index, e)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Number</Form.Label>
                      <Form.Control
                        type="text"
                        name="accountNumber"
                        value={bank.accountNumber}
                        onChange={(e) => handleBankChange(index, e)}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>IBAN</Form.Label>
                      <Form.Control
                        type="text"
                        name="IBAN"
                        value={bank.IBAN}
                        onChange={(e) => handleBankChange(index, e)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group>
                  <Form.Label>Currency</Form.Label>
                  <Form.Control
                    type="text"
                    name="currency"
                    value={bank.currency}
                    onChange={(e) => handleBankChange(index, e)}
                  />
                </Form.Group>
              </Card.Body>
            </Card>
          ))}
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : "Sign Up"}
        </Button>

        <p className="mt-3">
          Already have an account? <Link to="/login">Log in</Link>
        </p>
      </Form>
    </Container>
  );
};

export default SignUp;
